import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";

import { AppShell } from "@/components/layout/app-shell";
import { toast } from "@/components/ui/toast";
import { useAuth } from "@/hooks/use-auth";
import { EvergreenAuthProvider, useEvergreenAuth } from "@/contexts/evergreen-auth";
import { apiEvents } from "@/lib/api-events";

import InboxPage from "@/pages/inbox";
import LoginPage from "@/pages/login";
import VerifyPage from "@/pages/verify";

// Lazy: recharts + el portal solo se cargan cuando se abre /evergreen.
const EvergreenPage = lazy(() => import("@/pages/evergreen"));
const EvergreenLoginPage = lazy(() => import("@/pages/evergreen-login"));

function RouteSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="size-8 animate-spin rounded-full border-2 border-border border-t-brand" />
    </div>
  );
}

/**
 * Gate del vertical operaciones: usa la auth de Supabase (magic-link → JWT),
 * NO el login viejo del portal. Sin sesión Supabase → pantalla de login propia.
 * El Engine (api2) impone el tenant desde el JWT, así que no hace falta el
 * TenantGate viejo acá.
 */
function EvergreenGate() {
  const { session, loading } = useEvergreenAuth();
  if (loading) return <RouteSpinner />;
  if (!session) {
    return (
      <Suspense fallback={<RouteSpinner />}>
        <EvergreenLoginPage />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<RouteSpinner />}>
      <EvergreenPage />
    </Suspense>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, loading, signOut } = useAuth();
  const navigate = useNavigate();

  // 401 reactivo (mientras el usuario ya está usando el portal):
  // toast + redirect. La carga inicial usa validateSession con
  // suppressUnauthorizedEvent=true → no llega acá, se maneja silencioso
  // en AuthProvider.
  useEffect(() => {
    if (!session) return;
    function handleUnauthorized() {
      toast.error("Tu sesión expiró. Inicia sesión de nuevo.");
      signOut();
      navigate("/login", { replace: true });
    }
    apiEvents.addEventListener("unauthorized", handleUnauthorized);
    return () => {
      apiEvents.removeEventListener("unauthorized", handleUnauthorized);
    };
  }, [session, signOut, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-2 border-border border-t-brand" />
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// El dominio evergreen.aliadigital.co es la cara pública del vertical
// operaciones: su raíz (y cualquier ruta suelta) cae a /evergreen. Otros hosts
// (app.aliadigital.co) NO se tocan → siguen yendo a /inbox.
const IS_EVERGREEN_HOST =
  typeof window !== "undefined" &&
  window.location.hostname === "evergreen.aliadigital.co";
const HOME_PATH = IS_EVERGREEN_HOST ? "/evergreen" : "/inbox";

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/verify" element={<VerifyPage />} />

      {/* Vertical operaciones · auth propia de Supabase (JWT → Engine api2),
          full-screen, fuera del AppShell y del login viejo del portal. */}
      <Route
        path="/evergreen"
        element={
          <EvergreenAuthProvider>
            <EvergreenGate />
          </EvergreenAuthProvider>
        }
      />

      {/* Raíz host-aware: evergreen.aliadigital.co → /evergreen; resto → /inbox. */}
      <Route path="/" element={<Navigate to={HOME_PATH} replace />} />

      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/inbox/:conversationId" element={<InboxPage />} />
      </Route>

      <Route path="*" element={<Navigate to={HOME_PATH} replace />} />
    </Routes>
  );
}
